
import { useState, useEffect, useCallback } from 'react';
import { EcomPaymentSettings } from '@/lib/supabase/payments/types';
import { getShopPaymentSettings } from '@/lib/supabase/payments/settings/getShopPaymentSettings';
import { updateShopPaymentSettings } from '@/lib/supabase/payments/settings/updateShopPaymentSettings';
import { toast } from 'sonner';

export const usePaymentSettings = (shopId: string) => {
  const [paymentSettings, setPaymentSettings] = useState<EcomPaymentSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadPaymentSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const settings = await getShopPaymentSettings(shopId);
      setPaymentSettings(settings);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres de paiement:', error);
      toast.error('Impossible de charger les paramètres de paiement');
    } finally {
      setIsLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    loadPaymentSettings();
  }, [loadPaymentSettings]);

  const handleTogglePaymentMethod = async (
    method: 'cod' | 'stripe' | 'paypal',
    enabled: boolean
  ) => {
    if (!paymentSettings) return;

    setIsSaving(true);
    try {
      // Créer l'objet de mise à jour avec la clé dynamique
      const updateData = {
        [`${method}_enabled`]: enabled
      };

      // Mettre à jour localement pour UI immédiate
      setPaymentSettings({
        ...paymentSettings,
        ...updateData
      });

      // Mettre à jour dans la base de données
      const result = await updateShopPaymentSettings(shopId, updateData);

      if (!result.success) {
        // Restaurer l'état précédent en cas d'erreur
        setPaymentSettings(paymentSettings);
        toast.error(`Erreur lors de la mise à jour de ${method}`);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      // Restaurer l'état précédent
      setPaymentSettings(paymentSettings);
      toast.error('Erreur lors de la mise à jour des paramètres');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    paymentSettings,
    isLoading,
    isSaving,
    handleTogglePaymentMethod,
    loadPaymentSettings
  };
};
